/* import __COLOCATED_TEMPLATE__ from './manage-schedules.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { cached } from 'tracked-toolbox';
import { restartableTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { type TaskGenerator } from 'ember-concurrency';
import type IntlService from 'ember-intl/services/intl';
import type ManageSchedulesApi from 'embercom/lib/reporting/manage-schedules-api';
import type ScheduledExternalShare from 'embercom/models/reporting/scheduled-external-share';
import type IntercomConfirmService from 'embercom/services/intercom-confirm-service';
import type NotificationsService from 'embercom/services/notifications-service';
import type Store from '@ember-data/store';
import {
  getRolesData,
  getUsersData,
  getEmailsData,
} from 'embercom/utils/recipient-resolution-helpers';

export type SortDirection = 'asc' | 'desc';
export type SortableColumn = 'name' | 'report' | 'owner' | 'createdAt' | 'updatedAt';

interface TableColumn {
  label: string;
  valuePath: string;
  width?: string;
  minWidth?: string;
  isSortable?: boolean;
}

interface Args {
  model: ManageSchedulesApi;
}

export default class ManageSchedulesComponent extends Component<Args> {
  @service declare intl: IntlService;
  @service declare intercomConfirmService: IntercomConfirmService;
  @service declare notificationsService: InstanceType<typeof NotificationsService>;
  @service declare store: Store;
  @service declare permissionsService: any;

  @tracked sortBy?: SortableColumn;
  @tracked sortDirection?: SortDirection;
  @tracked isScheduleDeliveryDrawerOpen = false;
  @tracked scheduleToEdit?: ScheduledExternalShare;

  get api(): ManageSchedulesApi {
    return this.args.model;
  }

  get sortState() {
    return {
      valuePath: this.sortBy,
      direction: this.sortDirection,
    };
  }

  get schedules(): ScheduledExternalShare[] {
    return this.api.schedules;
  }

  @cached
  get tableColumns(): TableColumn[] {
    return [
      {
        label: this.intl.t('reporting.manage-schedules.table.name'),
        valuePath: 'name',
        width: '25%',
        minWidth: '260px',
        isSortable: true,
      },
      {
        label: this.intl.t('reporting.manage-schedules.table.report'),
        valuePath: 'report',
        width: '20%',
        minWidth: '220px',
        isSortable: true,
      },
      {
        label: this.intl.t('reporting.manage-schedules.table.recipients'),
        valuePath: 'recipients',
        width: '12%',
        minWidth: '120px',
        isSortable: false,
      },
      {
        label: this.intl.t('reporting.manage-schedules.table.owner'),
        valuePath: 'owner',
        width: '13%',
        minWidth: '120px',
        isSortable: false,
      },
      {
        label: this.intl.t('reporting.manage-schedules.table.created-at'),
        valuePath: 'createdAt',
        width: '8%',
        minWidth: '120px',
        isSortable: true,
      },
      {
        label: this.intl.t('reporting.manage-schedules.table.last-edited-at'),
        valuePath: 'updatedAt',
        width: '8%',
        minWidth: '120px',
        isSortable: true,
      },
      {
        label: this.intl.t('reporting.manage-schedules.table.actions'),
        valuePath: 'actions',
        width: '4%',
        minWidth: '64px',
        isSortable: false,
      },
    ];
  }

  get totalSchedules() {
    return this.api.totalCount;
  }

  get currentSchedulesCount() {
    if (this.schedules.length === 0 && this.api.previousSchedulesCount > 0) {
      return this.api.previousSchedulesCount;
    }
    return Math.min(this.schedules.length, this.totalSchedules);
  }

  get isInitialLoad() {
    return this.api.isLoadingPage && this.schedules.length === 0;
  }

  get isLoadingMore() {
    return (
      (this.api.isLoadingPage && this.schedules.length > 0) || taskFor(this.sortUpdate).isRunning
    );
  }

  @cached
  get actionMenuItemsForSchedule() {
    return (scheduleId: string) => {
      return [
        {
          items: [
            {
              text: this.intl.t('reporting.manage-schedules.actions.edit'),
              icon: 'edit',
              onSelectItem: () => this.editSchedule(scheduleId),
            },
            {
              text: this.intl.t('reporting.manage-schedules.actions.delete'),
              icon: 'trash',
              onSelectItem: () => this.deleteSchedule(scheduleId),
              isDestructive: true,
            },
          ],
        },
      ];
    };
  }

  @action
  editSchedule(scheduleId: string) {
    if (!this.permissionsService.currentAdminCan('can_export_csv')) {
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal('can_export_csv');
      return;
    }

    let schedule = this.schedules.find((s) => s.id === scheduleId);
    if (schedule) {
      this.scheduleToEdit = schedule;
      this.isScheduleDeliveryDrawerOpen = true;
    }
  }

  @action
  closeScheduleDeliveryDrawer() {
    this.isScheduleDeliveryDrawerOpen = false;
    this.scheduleToEdit = undefined;
  }

  @action
  handleScheduleUpdated(updatedScheduleData: ScheduledExternalShare) {
    this.api.updateScheduleInList(updatedScheduleData);
  }

  @action
  async deleteSchedule(scheduleId: string) {
    if (!this.permissionsService.currentAdminCan('can_export_csv')) {
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal('can_export_csv');
      return;
    }

    let confirmed = await this.intercomConfirmService.confirm({
      title: this.intl.t('reporting.manage-schedules.delete-confirm.title'),
      body: this.intl.t('reporting.manage-schedules.delete-confirm.body'),
      primaryButtonType: 'primary-destructive',
      confirmButtonText: this.intl.t('reporting.manage-schedules.delete-confirm.confirm-button'),
    });

    if (!confirmed) {
      return;
    }

    try {
      await this.api.deleteSchedule(scheduleId);
      this.notificationsService.notifyConfirmation(
        this.intl.t('reporting.manage-schedules.delete-success'),
      );
    } catch (error) {
      this.notificationsService.notifyError(this.intl.t('reporting.manage-schedules.delete-error'));
    }
  }

  @restartableTask
  *sortUpdate(sortBy: SortableColumn, sortDirection: SortDirection): TaskGenerator<void> {
    yield this.api.sortUpdate(sortBy, sortDirection);
  }

  @action
  onSort(clickedValuePath: string): void {
    if (clickedValuePath === this.sortBy) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = clickedValuePath as SortableColumn;
      this.sortDirection = 'desc';
    }

    taskFor(this.sortUpdate).perform(this.sortBy, this.sortDirection);
  }

  @action
  async loadMore() {
    if (!this.api.lastPageHit && !this.api.isLoadingPage) {
      await this.api.loadPage();
    }
  }

  get hasMoreSchedules(): boolean {
    return !this.api.lastPageHit;
  }

  get recipientsDataForSchedule() {
    return (schedule: ScheduledExternalShare) => ({
      roles: getRolesData(this.store, this.intl, schedule),
      users: getUsersData(this.store, this.intl, schedule),
      emails: getEmailsData(this.intl, schedule),
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::ManageSchedules': typeof ManageSchedulesComponent;
    'reporting/manage-schedules': typeof ManageSchedulesComponent;
  }
}
